import React, { useCallback, useEffect, useState } from "react";
import "./PWAInstallPrompt.css";

const INSTALLABLE_EVENT = "pwa-installable";
const INSTALLED_EVENT = "pwa-installed";

let deferredPrompt = null;

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event;
    window.dispatchEvent(new Event(INSTALLABLE_EVENT));
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    window.dispatchEvent(new Event(INSTALLED_EVENT));
  });
}

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.matchMedia("(display-mode: fullscreen)").matches ||
  window.navigator.standalone === true;

function waitForNativePrompt(timeoutMs = 8000) {
  if (deferredPrompt) return Promise.resolve(true);
  return new Promise((resolve) => {
    const done = (ok) => {
      window.removeEventListener(INSTALLABLE_EVENT, onReady);
      window.clearTimeout(timer);
      resolve(ok);
    };
    const onReady = () => done(true);
    const timer = window.setTimeout(() => done(Boolean(deferredPrompt)), timeoutMs);
    window.addEventListener(INSTALLABLE_EVENT, onReady);
  });
}

function PWAInstallPrompt() {
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      return undefined;
    }

    const onInstalled = () => {
      setInstalled(true);
      setDismissed(false);
    };
    window.addEventListener(INSTALLED_EVENT, onInstalled);

    return () => {
      window.removeEventListener(INSTALLED_EVENT, onInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    setInstalling(true);
    try {
      if (!deferredPrompt) {
        if (navigator.serviceWorker) {
          try {
            await navigator.serviceWorker.ready;
          } catch {
            /* ignore */
          }
        }
        await waitForNativePrompt();
      }
      if (!deferredPrompt) return;

      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      deferredPrompt = null;
      if (choice.outcome === "accepted") {
        setInstalled(true);
      }
    } finally {
      setInstalling(false);
    }
  }, []);

  const handleOpen = useCallback(() => {
    window.open(`${window.location.origin}/`, "_blank");
  }, []);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    setInstalled(false);
  }, []);

  if (isStandalone()) return null;
  if (dismissed) return null;

  return (
    <div
      className="pwaInstallBanner"
      role="dialog"
      aria-label={installed ? "Open Gallery Kiosk" : "Install Gallery Kiosk"}
    >
      <div className="pwaInstallBanner__content">
        <header className="pwaInstallBanner__header">
          <span className="pwaInstallBanner__badge">Kiosk setup</span>
          <p className="pwaInstallBanner__text">
            {installed ? "App installed" : "Install MOSIDA Gallery Kiosk"}
          </p>
          <p className="pwaInstallBanner__hint">
            {installed
              ? "MOSIDA Gallery Kiosk was added to your device."
              : "Install the app to the home screen for a faster, full-screen kiosk experience."}
          </p>
        </header>

        <div className="pwaInstallBanner__actions">
          <button
            type="button"
            className="pwaInstallBanner__btn pwaInstallBanner__btn--primary"
            onClick={installed ? handleOpen : handleInstall}
            disabled={installing}
          >
            {installed
              ? "Open app"
              : installing
                ? "Installing…"
                : "Install app"}
          </button>
          <button
            type="button"
            className="pwaInstallBanner__btn pwaInstallBanner__btn--secondary"
            onClick={handleDismiss}
            disabled={installing}
          >
            {installed ? "Close" : "Not now"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PWAInstallPrompt;
